// Normalise & merge helpers for IngredientLineRecord[]
//
// Critical rule: merge by id (server) not array index.
// For unsaved lines, use client_key.

export const normaliseLine = (line) => {
  const l = line || {};
  return {
    id: l.id ?? null,
    client_key: l.client_key ?? l.clientKey ?? null, // temp identity for new unsaved lines
    position: Number.isFinite(l.position) ? l.position : 0,
    raw_text: (l.raw_text ?? l.rawText ?? "").toString(),

    // backend fields (may be null/undefined early)
    rit_payload: l.rit_payload ?? null,
    confidence_score: l.confidence_score ?? null,
    warnings: Array.isArray(l.warnings) ? l.warnings : [],
    language_detected: l.language_detected ?? null,
    rit_model_version: l.rit_model_version ?? null,
    rit_kb_version: l.rit_kb_version ?? null,
    parsed_at: l.parsed_at ?? null,
    parse_status: l.parse_status ?? "pending",

    ingredient_id: l.ingredient_id ?? null,
    resolved_at: l.resolved_at ?? null,

    source: l.source ?? "rit_api",
  };
};

export const normaliseServerLines = (lines) => {
  if (!Array.isArray(lines)) return [];
  return lines.map(normaliseLine);
};

export const reindexPositions = (lines) => {
  return (lines || []).map((l, idx) => ({ ...l, position: idx }));
};

// Merge strategy:
// - If incoming has an id, match prev by id.
// - Else match by client_key (unsaved line).
// - Otherwise keep prev items that aren’t present in incoming (usually none after interpret).
export function mergeByIdOrClientKey(prev, incoming) {
  const prevArr = Array.isArray(prev) ? prev : [];
  const incArr = Array.isArray(incoming) ? incoming : [];

  const prevById = new Map(
    prevArr.filter(l => l?.id != null).map(l => [String(l.id), l])
  );

  const prevByClientKey = new Map(
    prevArr.filter(l => l?.client_key).map(l => [String(l.client_key), l])
  );

  const usedPrev = new Set();
  const out = [];

  // 1) Add/merge incoming on top of prev
  for (const inc of incArr) {
    const idKey = inc?.id != null ? String(inc.id) : null;
    const ckKey = inc?.client_key ? String(inc.client_key) : null;

    if (idKey && prevById.has(idKey)) {
      out.push({ ...prevById.get(idKey), ...inc });
      usedPrev.add(`id:${idKey}`);
      continue;
    }

    if (ckKey && prevByClientKey.has(ckKey)) {
      out.push({ ...prevByClientKey.get(ckKey), ...inc });
      usedPrev.add(`ck:${ckKey}`);
      continue;
    }

    // brand new from server
    out.push(inc);
  }

  // 2) Keep prev lines that server didn't mention (this prevents “clearing”)
  for (const p of prevArr) {
    const idKey = p?.id != null ? String(p.id) : null;
    const ckKey = p?.client_key ? String(p.client_key) : null;

    if (idKey && usedPrev.has(`id:${idKey}`)) continue;
    if (!idKey && ckKey && usedPrev.has(`ck:${ckKey}`)) continue;

    // if incoming already contains this id, skip (safety)
    if (idKey && incArr.some(x => x?.id != null && String(x.id) === idKey)) continue;
    if (!idKey && ckKey && incArr.some(x => x?.client_key && String(x.client_key) === ckKey)) continue;

    out.push(p);
  }

  return out;
}

// Payload for interpret endpoint: [{id?, raw_text, source}]
export const toInterpretPayload = (lines) => {
  return (lines || [])
    .map((l) => normaliseLine(l))
    .filter((l) => l.raw_text.trim().length > 0)
    .map((l) => ({
      id: l.id ?? null,
      raw_text: l.raw_text.trim(),
      source: l.source ?? null,
    }));
};